/**
 * CommunityReportPage - Homeowner self-reporting of community concerns (Beta)
 * Mobile-optimized with camera capture support, AI photo analysis, one-click submit
 */
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Camera, Upload, Loader2, ArrowLeft, Sparkles,
  Send, CheckCircle, MapPin, AlertTriangle, Eye, X
} from "lucide-react";

const CONCERN_TYPES = [
  { value: "landscaping", label: "Landscaping" },
  { value: "parking", label: "Parking" },
  { value: "trash", label: "Trash / Debris" },
  { value: "exterior_change", label: "Unauthorized Exterior Change" },
  { value: "fencing", label: "Fencing" },
  { value: "signage", label: "Signage" },
  { value: "structure", label: "Unauthorized Structure" },
  { value: "safety", label: "Safety Concern" },
  { value: "noise", label: "Noise / Nuisance" },
  { value: "other", label: "Other" },
];

const CommunityReportPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1); // 1: photo, 2: analysis, 3: details, 4: done
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [description, setDescription] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState("");
  const [fileId, setFileId] = useState(null);
  const [concernType, setConcernType] = useState("other");
  const [severity, setSeverity] = useState("medium");
  const [location, setLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleAnalyze = async () => {
    if (!imageFile) return;
    try {
      setAnalyzing(true);
      const formData = new FormData();
      formData.append("file", imageFile);
      if (description) formData.append("description", description);

      const res = await axios.post(`${API}/community-reports/analyze-photo`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        timeout: 60000
      });

      setAnalysisResult(res.data.analysis);
      setFileId(res.data.file_id);
      setStep(2);
      toast.success("Photo analyzed successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await axios.post(`${API}/community-reports/submit`, {
        file_id: fileId,
        description,
        ai_analysis: analysisResult,
        concern_type: concernType,
        severity,
        location
      }, { headers: { Authorization: `Bearer ${token}` } });

      setStep(4);
      toast.success("Report submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setImageFile(null);
    setImagePreview(null);
    setDescription("");
    setAnalysisResult("");
    setFileId(null);
    setConcernType("other");
    setSeverity("medium");
    setLocation("");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="community-report-page">
      {/* Header */}
      <header className="border-b bg-card/95 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/owner")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-semibold text-base">Report a Concern</h1>
            <p className="text-xs text-muted-foreground">Help keep our community beautiful</p>
          </div>
          <Badge variant="outline" className="text-violet-600 border-violet-200 bg-violet-50">BETA</Badge>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`flex-1 h-1.5 rounded-full transition-colors ${step >= s ? "bg-primary" : "bg-muted"}`} />
          ))}
        </div>

        {/* Step 1: Upload Photo */}
        {step === 1 && (
          <Card data-testid="report-step-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                Take or Upload a Photo
              </CardTitle>
              <CardDescription>Snap a photo of the community concern</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!imagePreview ? (
                <div className="space-y-3">
                  <label
                    className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-xl cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                    data-testid="report-upload-area"
                  >
                    <Camera className="w-12 h-12 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">Tap to take photo or upload</p>
                    <p className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handleImageSelect}
                      data-testid="report-file-input"
                    />
                  </label>
                </div>
              ) : (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" className="w-full max-h-64 object-contain rounded-lg border" data-testid="report-image-preview" />
                  <Button variant="outline" size="icon" className="absolute top-2 right-2 h-8 w-8 bg-background/80" onClick={handleReset}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              <div>
                <Label>What did you notice? (optional)</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what you see..."
                  rows={2}
                  data-testid="report-description-input"
                />
              </div>

              <Button
                onClick={handleAnalyze}
                disabled={!imageFile || analyzing}
                className="w-full"
                size="lg"
                data-testid="report-analyze-btn"
              >
                {analyzing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing with AI...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Analyze Photo</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: AI Analysis Result */}
        {step === 2 && (
          <Card data-testid="report-step-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-600" />
                AI Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-violet-50/50 border border-violet-200 rounded-lg text-sm whitespace-pre-wrap" data-testid="report-analysis-text">
                {analysisResult}
              </div>

              {imagePreview && (
                <img src={imagePreview} alt="Concern" className="w-full max-h-32 object-contain rounded-lg border" />
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleReset} className="flex-1">Retake Photo</Button>
                <Button onClick={() => setStep(3)} className="flex-1" data-testid="report-continue-btn">
                  Continue <Send className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Details & Submit */}
        {step === 3 && (
          <Card data-testid="report-step-3">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Report Details
              </CardTitle>
              <CardDescription>Provide additional details for the board</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Concern Type</Label>
                <Select value={concernType} onValueChange={setConcernType}>
                  <SelectTrigger data-testid="report-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONCERN_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Severity</Label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger data-testid="report-severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - Minor/Cosmetic</SelectItem>
                    <SelectItem value="medium">Medium - Notable Concern</SelectItem>
                    <SelectItem value="high">High - Safety/Significant</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Location (optional)</Label>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Near Lot 23, Main entrance"
                    data-testid="report-location-input"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">Back</Button>
                <Button onClick={handleSubmit} disabled={submitting} className="flex-1" data-testid="report-submit-btn">
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Submit Report</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <Card className="border-green-200 bg-green-50/30" data-testid="report-step-4">
            <CardContent className="py-8 text-center space-y-4">
              <CheckCircle className="w-14 h-14 text-green-600 mx-auto" />
              <h2 className="text-lg font-bold text-green-800">Report Submitted!</h2>
              <p className="text-sm text-green-700">
                Thank you for helping keep our community beautiful. The board will review your concern shortly.
              </p>
              <div className="flex gap-2 justify-center pt-2">
                <Button variant="outline" onClick={handleReset} data-testid="report-another-btn">Report Another</Button>
                <Button onClick={() => navigate("/owner")} data-testid="report-back-home-btn">Back to Home</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CommunityReportPage;
